import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

import BreadcrumbsNav from "@/components/bread-crumb-nav";
import GeneralCard from "@/components/general-card";
import PageHead from "@/components/PageHead";
import ScrollProgressBar from "@/components/scroll-progress-bar";
import SearchBox from "@/components/search-box";
import SmallTitle from "@/components/mini-components/SmallTitle";
import TitleWithImgFirst from "@/components/title-img-first";
import { useI18n } from "@/i18n/I18nProvider";
import { getBlogsList } from "../../../locales/blogs";
import { styles as tabStyle } from "@/sections/home/image_section";
import { convertSpacesToHyphens } from "@/utils/general-functions";
import { paginate } from "@/utils/pagnation";
import { colors } from "@/utils/const";

const BLOGS_PER_PAGE = 10;

/* A search only starts once the query is long enough to be meaningful. */
const MIN_SEARCH_LENGTH = 2;

/* The featured block shows one large blog plus this many in the side list. */
const SIDE_BLOGS_COUNT = 4;

const paginationPalette = {
  primary: { main: colors.blue },
  secondary: { main: colors.orange },
};

const themeRtl = createTheme({ direction: "rtl", palette: paginationPalette });
const themeLtr = createTheme({ direction: "ltr", palette: paginationPalette });

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const headerSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  my: 3,
};

const featuredGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "2.5fr 2fr" },
  gap: 5,
  width: "100%",
  borderRadius: "16px",
};

const featuredFrameSx = {
  position: "relative",
  borderRadius: "16px",
  overflow: "hidden",
};

const featuredImageSx = { width: "100%", height: "100%", objectFit: "cover" };

const featuredScrimSx = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))",
};

const featuredCaptionSx = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  color: "white",
  p: 3,
  zIndex: 2,
};

const featuredTitleSx = {
  fontSize: { lg: "32px", xs: "28px" },
  fontWeight: "bold",
};

const featuredMetaSx = { display: "flex", gap: 5, mt: 2, color: "white" };

const sideListSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
};

const sideListHeadingSx = {
  fontSize: "16px",
  color: colors.navy_blue,
  fontWeight: 600,
  mb: 2,
};

const sideRowSx = {
  display: "flex",
  gap: 2,
  height: "116px",
  my: 1,
  borderBottom: `1px solid ${colors?.light_grey}`,
  alignItems: "center",
};

const sideThumbSx = {
  width: "110px",
  height: "94px",
  borderRadius: "8px",
  overflow: "hidden",
};

const sideThumbInnerStyle = {
  position: "relative",
  width: "100%",
  height: "100%",
  cursor: "pointer",
};

const sideTextSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "center", sm: "space-between" },
  fontSize: "14px",
  flex: 1,
};

const sideTitleStyle = {
  fontWeight: "bold",
  color: "#111827",
  textDecoration: "none",
};

const sideMetaSx = {
  display: "flex",
  justifyContent: "space-between",
  mt: 2,
  color: "#686B7A",
};

const cardsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  gap: 5,
  mt: { md: 4 },
};

const paginationRowSx = {
  py: 3,
  width: "100%",
  justifyContent: "center",
  textAlign: "center",
};

const emptyStateSx = {
  fontSize: 24,
  my: 8,
  color: colors?.orange,
  textAlign: "center",
  width: "100%",
  fontWeight: "600",
};

const blogHref = (blog) => `/blogs/${blog?.category?.inPath}/${blog?.path}`;

export default function BlogsPage() {
  const router = useRouter();
  const { t, locale } = useI18n();

  const blogsList = getBlogsList(locale);

  const categories = useMemo(
    () => [...new Set(blogsList.map((blog) => blog?.category?.name))],
    [blogsList],
  );

  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");

  /* `null` means "not searching"; an empty array means "searched, no hits". */
  const [searchResults, setSearchResults] = useState(null);

  const categoryListRef = useRef(null);

  const featuredBlog = blogsList[0];
  const sideBlogs = blogsList.slice(1, 1 + SIDE_BLOGS_COUNT);

  // Deep links such as /blogs?cat=Financing open that tab and scroll to it.
  useEffect(() => {
    if (!router.isReady) return;

    const category = router.query?.cat;
    if (!category) return;

    setActiveCategory(category);

    requestAnimationFrame(() => {
      categoryListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    router.replace("/blogs", undefined, { shallow: true, scroll: false });
  }, [router.isReady, router.query, router]);

  const resultsByCategory = useMemo(() => {
    if (!searchResults) return null;

    return searchResults.reduce((grouped, blog) => {
      const category = blog.category?.name;
      (grouped[category] ||= []).push(blog);
      return grouped;
    }, {});
  }, [searchResults]);

  const displayedCategories = resultsByCategory
    ? Object.keys(resultsByCategory)
    : categories;

  const displayedBlogs = useMemo(() => {
    if (resultsByCategory) return resultsByCategory[activeCategory] || [];

    return blogsList.filter((blog) => blog.category?.name === activeCategory);
  }, [resultsByCategory, activeCategory, blogsList]);

  const [pageBlogs, pageCount] = paginate(
    displayedBlogs,
    currentPage,
    BLOGS_PER_PAGE,
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;

    setSearchValue(query);
    setCurrentPage(1);

    const trimmed = query.trim().toLowerCase();

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setSearchResults(null);
      setActiveCategory(categories[0]);
      return;
    }

    const matches = blogsList.filter((blog) =>
      blog.title?.toLowerCase().includes(trimmed),
    );

    setSearchResults(matches);
    setActiveCategory(matches[0]?.category?.name ?? categories[0]);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  /* Stable so the memoised blog cards are not re-rendered on every keystroke. */
  const handleAuthorClick = useCallback(
    (author) => {
      router.push(`/blogs/authors/${convertSpacesToHyphens(author)}`);
    },
    [router],
  );

  // Paging jumps back to the top, but not on the first render — that would
  // fight the scroll-into-view above when arriving with ?cat=.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    window.scrollTo(0, 0);
  }, [currentPage]);

  const isSearching = searchResults !== null;
  const hasResults = displayedBlogs.length > 0;

  /*
   * The category strip is scrolled to from ?cat= links, so it has to clear the
   * sticky header — whose height varies by viewport and, because the Arabic
   * menu is wider, by locale.
   */
  const is1170to1200 = useMediaQuery("(min-width:1170px) and (max-width:1200px)");
  const is1230to1306 = useMediaQuery("(min-width:1230px) and (max-width:1306px)");
  const isAbove1306 = useMediaQuery("(min-width:1306px)");
  const isAbove900 = useMediaQuery("(min-width:900px)");

  const isArabic = locale === "ar";

  let scrollMarginTop = 96;

  if (isAbove1306 || (isArabic && (is1230to1306 || is1170to1200))) {
    scrollMarginTop = 130;
  } else if (isAbove900) {
    scrollMarginTop = 177;
  }

  const PaginationControl = (
    <Pagination
      count={pageCount}
      page={currentPage}
      onChange={(event, page) => setCurrentPage(page)}
      color="primary"
    />
  );

  return (
    <>
      <PageHead
        title={t.pages_main_tags.blogs.title}
        description={t.pages_main_tags.blogs.meta_desc}
      />

      {/* <ScrollProgressBar height={5} contentSelector="#main-content" /> */}

      <main id="main-content">
        <Box className="horizontal-safe-margin">
          <BreadcrumbsNav
            iconLink="/blogs"
            links={[{ href: "/blogs", label: t.blogs.title }]}
          />

          <Box sx={headerSx}>
            <TitleWithImgFirst h1="h1" title={t.blogs.latestBlog} />

            <SearchBox
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={locale === "ar" ? "ابحث" : "Search"}
              locale={locale}
              inputProps={{ "aria-label": locale === "ar" ? "ابحث" : "Search" }}
              sx={{ mt: { xs: 0, md: 1 } }}
            />
          </Box>

          {!isSearching && featuredBlog && (
            <Box sx={featuredGridSx}>
              <Link
                href={blogHref(featuredBlog)}
                style={{ textDecoration: "none" }}
              >
                <Box sx={featuredFrameSx}>
                  {/*
                   * Largest element above the fold, so it loads eagerly and at
                   * high priority instead of waiting for the lazy-load pass.
                   */}
                  <Box
                    component="img"
                    src={featuredBlog.img}
                    alt={featuredBlog.title}
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    sx={featuredImageSx}
                  />

                  <Box sx={featuredScrimSx} />

                  <Box sx={featuredCaptionSx}>
                    <Typography sx={featuredTitleSx}>
                      {featuredBlog.title}
                    </Typography>

                    <Box sx={featuredMetaSx}>
                      <Typography sx={{ fontSize: "14px" }}>
                        {featuredBlog.author}
                      </Typography>

                      <Typography
                        sx={{ fontSize: "14px" }}
                        dir={locale === "ar" ? "rtl" : "ltr"}
                      >
                        {" "}
                        {featuredBlog.date}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Link>

              <Box sx={sideListSx}>
                <Typography sx={sideListHeadingSx}>
                  {t?.blogs?.features}
                </Typography>

                {sideBlogs.map((blog) => (
                  <Box key={blog.path} sx={sideRowSx}>
                    <Box component={Link} href={blogHref(blog)} sx={sideThumbSx}>
                      <div style={sideThumbInnerStyle}>
                        <Image
                          src={blog.img}
                          alt={blog.title}
                          fill
                          sizes="110px"
                          loading="lazy"
                          style={{ objectFit: "contain" }}
                        />
                      </div>
                    </Box>

                    <Box sx={sideTextSx}>
                      <Link href={blogHref(blog)} style={sideTitleStyle}>
                        {blog.title}
                      </Link>

                      <Box sx={sideMetaSx}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            textTransform: "capitalize",
                            cursor: "pointer",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleAuthorClick(blog.author);
                          }}
                        >
                          {blog.author}
                        </Typography>

                        <Typography sx={{ fontSize: "14px" }}>
                          {blog.date}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box
            className="vertical-safe-padding"
            id="scroll"
            ref={categoryListRef}
            sx={{ scrollMarginTop: `${scrollMarginTop}px` }}
          >
            {!isSearching && <SmallTitle h2="h2" title={t.blogs.title} />}

            <Box sx={tabStyle.btnContainer}>
              {displayedCategories.map((category) => (
                <Box
                  key={category}
                  component="button"
                  type="button"
                  aria-pressed={category === activeCategory}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    ...(category === activeCategory
                      ? tabStyle.btnActive
                      : tabStyle.btn),
                    mt: "16px",
                  }}
                >
                  {/*
                   * A span, not a heading: these are filter controls, and five
                   * <h3>s here broke the page's heading outline.
                   */}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: category === activeCategory ? "700" : "600",
                    }}
                  >
                    {category}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={cardsRowSx}>
              <Grid container spacing={{ md: 6 }}>
                {hasResults &&
                  pageBlogs.map((blog, index) => (
                    <Grid key={blog.path} item xs={12} md={6} lg={4}>
                      <GeneralCard
                        href={blogHref(blog)}
                        img={blog.img}
                        title={blog.title}
                        intro={blog.intro}
                        author={blog.author}
                        date={blog.date}
                        onAuthorClick={handleAuthorClick}
                      />

                      <Box
                        sx={{
                          borderBottom:
                            index === pageBlogs.length - 1
                              ? "none"
                              : { xs: "2px solid #ebebed", md: "none" },
                        }}
                      />
                    </Grid>
                  ))}

                {!hasResults && isSearching && (
                  <Typography sx={emptyStateSx}>
                    {locale === "ar"
                      ? "لا يوجد مدونات بهذا الاسم"
                      : "No blogs found"}
                  </Typography>
                )}
              </Grid>
            </Box>

            <Box sx={paginationRowSx}>
              {pageCount > 1 &&
                hasResults &&
                (locale === "ar" ? (
                  <CacheProvider value={cacheRtl}>
                    <ThemeProvider theme={themeRtl}>
                      {PaginationControl}
                    </ThemeProvider>
                  </CacheProvider>
                ) : (
                  <ThemeProvider theme={themeLtr}>
                    {PaginationControl}
                  </ThemeProvider>
                ))}
            </Box>
          </Box>
        </Box>
      </main>
    </>
  );
}
